import { Box} from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { useQueryClient } from '@tanstack/react-query';
import AdminDataGrid from 'csam/admin/components/AdminDataGrid';
import AdminPageHeader from 'csam/admin/components/AdminHeader';
import AdminPageLoader from 'csam/admin/components/AdminPageLoader';
import AdminWrapper from 'csam/admin/components/AdminWrapper';
import { getCommonColumns } from 'csam/admin/components/CommonColumns';
import AdminInternalError from 'csam/admin/pages/AdminInternalError';
import { useAuthenticatedMutation, useAuthenticatedQuery } from 'csam/api/api';
import LocaleContext from 'csam/components/LocaleContext';
import { fixed } from 'csam/utils/Constants';
import { useContext, useState } from 'react';
import { toast } from 'react-toastify';

const DigitalEthics = () => {
  const { locale, setLocale } = useContext(LocaleContext);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const deleteMutation = useAuthenticatedMutation<string[], void>(['delete-ethics'], (ids) => ({
    url: `${fixed}admin/${locale}/gisp_digital_ethics`,
    method: 'DELETE',
    data: { ids },
  }));

  const queryClient = useQueryClient();
  const handleDelete = (ids?: string[]) => {
    const idsToDelete = ids || selectedIds;
    deleteMutation.mutate(idsToDelete.map((id) => id.toString()) as unknown as void, {
      onSuccess: () => {
        toast.success(`Record Deleted Successfully`);
        queryClient.invalidateQueries({
          queryKey: ['DigitalEthicsPage', 'DigitalEthicsData', locale],
        });
      },
    });
  };

  const columns: GridColDef[] = [
    {
      field: 'id',
      headerName: 'Id',
      filterable: false,
      width: 40,
      renderCell: (params) => params.value,
    },

    {
      field: 'status',
      headerName: 'Status',
      width: 80,
      renderCell: (params) => (Number(params.value) === 1 ? 'Active' : 'Inactive'),
    },
    { field: 'title', headerName: 'Title' },
    { field: 'link', headerName: 'link' },
    { field: 'description', headerName: 'Description' },

    ...getCommonColumns({ handleDelete }),
  ];

  interface EthicsData {
    id: number;
    title: string;
    link: string;
    description: string;
    created_at: string;
    status: number;
  }

  interface EthicsDataResponse {
    success: boolean;
    data: EthicsData;
    translatableFields: string[];
  }

  const relations = ['year'];
  const relationsParam = relations.join(',');

  const localeFromStorage = localStorage.getItem('locale') || 'en';
  const { isPending, error, data } = useAuthenticatedQuery<EthicsDataResponse[]>(
    ['DigitalEthicsPage', 'DigitalEthicsData', locale],
    {
      url: `${fixed}admin/${locale || localeFromStorage}/gisp_digital_ethics?relations=${relationsParam}`,
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    },
  );

  if (isPending) return <AdminPageLoader />;
  if (error) return <Box>Error: {error.message}</Box>;
  if (data && !data.success) return <AdminInternalError />;

  return (
    <AdminWrapper>
        <AdminPageHeader
          title="GISP Digital Ethics"
          locale={locale}
          setLocale={setLocale}
          createLink="create"
          enableBulkDelete
        />
        <AdminDataGrid<EthicsDataResponse>
          data={data}
          columns={columns}
          setSelectedIds={setSelectedIds}
        />
    </AdminWrapper>
  );
};

export default DigitalEthics;
